import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios
      .get(`${API}/insights`)
      .then((res) => setPosts(res.data.posts || []))
      .catch(() => setPosts([]));
  }, []);

  const categories = ["All", ...Array.from(new Set(posts.map((p) => p.category)))];
  const filtered = filter === "All" ? posts : posts.filter((p) => p.category === filter);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid="insights-page">
      <SEO
        title="Insights — Technology Perspectives from Notion Minds Systems"
        description="Field notes from the GCC frontier — frameworks, perspectives and lessons learned from Notion Minds Systems engagements in AI, cybersecurity, cloud and OT."
        canonical="https://notionminds.sa/insights"
      />
      <section className="pt-24 pb-12 max-w-[1440px] mx-auto px-6 lg:px-12">
        <p className="font-mono-accent text-xs text-[#d4af37] mb-5">[ Insights ]</p>
        <h1 className="font-display text-5xl lg:text-7xl font-light leading-[1.05] max-w-4xl">
          Field notes from the <span className="text-gradient-gold font-semibold">Saudi, Omani & Qatari frontier.</span>
        </h1>
        <p className="mt-8 text-white/65 text-lg max-w-2xl">
          Perspectives, frameworks and lessons-learned from real engagements —
          published by our partners and senior practitioners.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24">
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`insight-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-xs font-mono-accent border transition-colors ${
                filter === c
                  ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/5"
                  : "border-white/15 text-white/60 hover:border-white/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {featured && (
          <motion.a
            key={featured.id}
            href="#"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid={`insight-featured-${featured.id}`}
            className="group grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 border border-[#1a1a20] hover:border-[#d4af37]/50 transition-colors"
          >
            <div className="lg:col-span-7 aspect-[16/10] lg:aspect-auto overflow-hidden relative">
              <img
                src={featured.image}
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent" />
            </div>
            <div className="lg:col-span-5 p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-5">
                <span className="font-mono-accent text-[10px] text-[#d4af37]">FEATURED</span>
                <span className="font-mono-accent text-[10px] text-white/40">·</span>
                <span className="font-mono-accent text-[10px] text-white/50">{featured.category}</span>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-light leading-tight mb-5 group-hover:text-[#d4af37] transition-colors">
                {featured.title}
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">{featured.excerpt}</p>
              <div className="flex items-center gap-4 text-xs text-white/40 font-mono-accent">
                <span>{featured.date}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {featured.read_time}</span>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 text-[#d4af37] text-sm">
                Read article <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
            </div>
          </motion.a>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((p, i) => (
            <motion.a
              key={p.id}
              href="#"
              data-testid={`insight-${p.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group border border-[#1a1a20] hover:border-[#d4af37]/50 transition-all overflow-hidden bg-[#0a0a0e]"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={p.image}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6">
                <p className="font-mono-accent text-[10px] text-[#d4af37] mb-3">{p.category}</p>
                <h3 className="font-display text-xl font-light leading-snug mb-3 group-hover:text-[#d4af37] transition-colors">
                  {p.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed mb-5 line-clamp-3">{p.excerpt}</p>
                <div className="flex items-center gap-4 text-[10px] text-white/40 font-mono-accent">
                  <span>{p.date}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.read_time}</span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>
    </div>
  );
}
